import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * The signed-in user, persisted across visits.
 *  - anonymous:     first visit — lands on the sign-in screen
 *  - authenticated: phone verified via OTP
 * There is no guest/practice tier: every session is a real phone identity,
 * because every balance on the platform is real money.
 */
export type SessionStatus = 'anonymous' | 'authenticated';

export type SessionUser = {
  id: string;
  phone: string;
  name: string | null;
  handle: string | null;
  kycVerified: boolean;
};

type SessionState = {
  status: SessionStatus;
  phone: string | null;
  token: string | null;
  user: SessionUser | null;
  hydrated: boolean;

  completeLogin: (args: { token: string; user: SessionUser }) => void;
  setUser: (user: SessionUser) => void;
  logout: () => void;
  _setHydrated: () => void;
};

export const useSession = create<SessionState>()(
  persist(
    (set) => ({
      status: 'anonymous',
      phone: null,
      token: null,
      user: null,
      hydrated: false,

      completeLogin: ({ token, user }) =>
        set({ status: 'authenticated', phone: user.phone, token, user }),
      setUser: (user) => set((s) => (s.user ? { ...s, user } : s)),
      logout: () => set({ status: 'anonymous', phone: null, token: null, user: null }),
      _setHydrated: () => set({ hydrated: true }),
    }),
    {
      name: 'dabba-session',
      partialize: (s) => ({ status: s.status, phone: s.phone, token: s.token, user: s.user }),
      onRehydrateStorage: () => (state) => {
        // A session persisted before practice mode was removed may carry
        // status 'practice' (a guest token). Those accounts are disabled
        // server-side; sign the browser out rather than resurrecting them.
        if (state && (state.status as string) !== 'authenticated' && state.token) state.logout();
        state?._setHydrated();
      },
    },
  ),
);

/**
 * What the UI may ASSUME about the viewer. 'unknown' while the persisted
 * session rehydrates — anything that routes or renders a signed-out state
 * must branch on this, never on a bare `!token`, or the first paint after a
 * reload bounces a signed-in user to the login screen.
 */
export type AuthGate = 'unknown' | 'signedOut' | 'authenticated';

export function useAuthGate(): AuthGate {
  return useSession((s) => {
    if (!s.hydrated) return 'unknown';
    return s.status === 'authenticated' && s.token ? 'authenticated' : 'signedOut';
  });
}
